import React from 'react'
import { useDispatch } from 'react-redux'
import { Avatar, Box, IconButton } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import TableData from '../atoms/table/TableData'
import ButtonCustom from '../atoms/button/ButtonCustom'
import Response from '../atoms/Response/Response'
import StatusBtn from '../atoms/status/StatusBtn'
import KebabMenu from '../dataGrid/KebabMenu'
import { formatCommonDate } from '../../util/formatDateAndTime'
import { formatCommonAmount } from '../../util/formatAmount'
import { openModal, setModalData } from '../../features/commonSlice'
import { formatPackageData, totalFromTree } from './modal/reviewLineItemTree'
import { statusData } from '../../util/constants'

export default function ProposalTable({ rows, makeBid = false }) {
	const dispatch = useDispatch()
	const navigate = useNavigate()

	const columns = [
		{
			field: 'kazooName',
			headerName: 'Couple',
			flex: 1.5,
			minWidth: 220,
			renderCell: (params) => (
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<Avatar src={params.row.image} alt={params.value} />
					{params.value}
				</div>
			),
		},
		{
			field: 'date',
			headerName: 'Date',
			flex: 1,
			minWidth: 100,
			renderCell: (params) => <p>{formatCommonDate(params.value)}</p>,
		},
		{ field: 'guests', headerName: 'Guests', flex: 0.6, minWidth: 80 },
		{
			field: 'packageDetail',
			headerName: 'Package',
			flex: 1.5,
			minWidth: 120,
			renderCell: (params) =>
				params.row.lineItems?.map((a) => a.name).join(', ') ?? '--',
		},
		/* TODO:: Uncomment when required */
		// {
		// 	field: 'subCategory',
		// 	headerName: 'Category Details',
		// 	flex: 2.5,
		// 	minWidth: 240,
		// 	renderCell: (params) => (
		// 		<div
		// 			style={{
		// 				display: 'flex',
		// 				alignItems: 'center',
		// 				height: '100%',
		// 			}}
		// 		>
		// 			<p>
		// 				{params.row.category && params.value
		// 					? `${params.row.category} : ${params.value}`
		// 					: '--'}
		// 			</p>
		// 		</div>
		// 	),
		// },
		{
			field: 'budget',
			headerName: 'Budget',
			flex: 1,
			minWidth: 120,
			valueGetter: (value, row) => {
				if (row?.price) return row?.price
				else if (row?.rawTotal) return row?.rawTotal
				else return 0
			},
			renderCell: (params) => <p>{formatCommonAmount(params.value)}</p>,
		},
		{
			field: 'status',
			headerName: 'Status',
			flex: 0.6,
			minWidth: 80,
			renderCell: (params) => (
				<>
					<StatusBtn
						label={params.value}
						variant={
							params.value === 'Approved'
								? 'completed'
								: params.value === 'In Review'
									? 'pending'
									: params.value === 'Submitted'
										? 'inprogress'
										: ['Declined', 'Rejected'].includes(
													params.value
											  )
											? 'error'
											: 'default'
						}
						size="small"
					/>
				</>
			),
		},
		/* TODO:: Uncomment when required */
		// {
		// 	field: 'responseLabel',
		// 	headerName: 'Response Rating',
		// 	flex: 1.2,
		// 	minWidth: 150,
		// 	renderCell: (params) => (
		// 		<div
		// 			style={{
		// 				display: 'flex',
		// 				alignItems: 'center',
		// 				height: '100%',
		// 			}}
		// 		>
		// 			<Response
		// 				size="sm"
		// 				label={params?.value || 'Very Responsive'}
		// 			/>
		// 		</div>
		// 	),
		// },
		...(rows.some((row) => row.label)
			? [
					{
						field: 'label',
						headerName: 'Status',
						flex: 1,
						minWidth: 120,
						renderCell: (params) => {
							const status = statusData.find(
								(item) => item.status === params.value
							)
							return (
								<StatusBtn
									size="small"
									label={params.value}
									variant={status?.variant || 'default'}
								/>
							)
						},
					},
				]
			: []),
		{
			field: ' ',
			headerName: ' ',
			flex: 1,
			minWidth: 120,
			align: 'right',
			filterable: false,
			sortable: false,
			renderCell: (params) => (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						height: '100%',
					}}
				>
					<ButtonCustom
						size="sm"
						variant={makeBid ? 'default' : 'outlined'}
						onClick={() => {
							if (makeBid) {
								dispatch(openModal('makeBid'))
								dispatch(setModalData(params.row))
							} else {
								navigate(`/vendor-proposal/${params?.id}`)
							}
						}}
					>
						{makeBid && <img src="/images/Bid.svg" alt="bid" />}
						{makeBid ? 'Make Bid' : 'View Detail'}
					</ButtonCustom>
				</div>
			),
		},

		{
			field: 'menu',
			headerName: '',
			sortable: false,
			filterable: false,
			width: 60,
			align: 'right',
			renderCell: (params) => (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						width: '100%',
						height: '100%',
					}}
				>
					<IconButton
						data-cy="kebab"
						size="small"
						onClick={(e) => openMenu(e, params.row)}
						sx={{ p: 0.5 }}
					>
						<img src="/images/more.svg" alt="" />
					</IconButton>
				</Box>
			),
		},
	]

	const [menuAnchor, setMenuAnchor] = React.useState(null)

	const openMenu = (e, row) => {
		e.stopPropagation()
		setMenuAnchor(e.currentTarget)
	}

	const closeMenu = () => {
		setMenuAnchor(null)
	}

	return (
		<div style={{ gridTemplateColumns: '1fr 1fr' }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					'@media (max-width: 900px)': {},
				}}
			>
				<Box sx={{ flex: 1, minHeight: 0 }}>
					<TableData
						getRowId={(row) => row.proposalId}
						rows={rows}
						columns={columns}
						sx={{
							height: 'calc(100dvh - 200px)',
							'@media (max-width: 768px)': {
								height: 'calc(100dvh - 260px)',
							},
						}}
					/>
				</Box>

				<KebabMenu
					anchorEl={menuAnchor}
					open={Boolean(menuAnchor)}
					onClose={closeMenu}
					items={[
						{
							key: 'Chat',
							label: 'Chat',
							icon: (
								<img src="/images/chat-icon.svg" alt="chat" />
							),
							onClick: () => navigate(`/chat`),
						},
					]}
				/>
			</Box>
		</div>
	)
}
